import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ChevronDown, Globe, Search } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const LanguageSwitcher = ({ compact = false }) => {
  const { lang, setLanguage, languages, source, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = languages.find((l) => l.code === lang) || languages[0];
  const filtered = languages.filter(
    (l) =>
      l.name.toLowerCase().includes(query.toLowerCase()) ||
      l.nativeName.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-switcher-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-sky-200/80 bg-white/80 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm backdrop-blur transition-colors hover:border-sky-400 hover:text-sky-700"
      >
        <Globe className="h-4 w-4 text-sky-600" />
        <span>{current.nativeName}</span>
        {source === "geo" && (
          <span className="hidden rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 sm:inline">
            {t.switcher.auto}
          </span>
        )}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="language-switcher-panel"
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.18 }}
            className="absolute end-0 z-50 mt-3 w-80 overflow-hidden rounded-2xl border border-sky-100 bg-white/95 shadow-2xl shadow-sky-900/10 backdrop-blur-xl"
          >
            <div className="border-b border-sky-50 p-3">
              <div className="flex items-center gap-2 rounded-xl bg-sky-50 px-3 py-2">
                <Search className="h-4 w-4 text-sky-500" />
                <input
                  data-testid="language-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.switcher.search}
                  className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400"
                />
              </div>
            </div>
            <div className="grid max-h-72 grid-cols-2 gap-1 overflow-y-auto p-3">
              {filtered.map((l) => (
                <button
                  key={l.code}
                  data-testid={`lang-option-${l.code}`}
                  onClick={() => {
                    setLanguage(l.code);
                    setOpen(false);
                    setQuery("");
                  }}
                  className={`flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-start text-sm transition-colors ${
                    l.code === lang
                      ? "bg-sky-600 text-white"
                      : "text-slate-700 hover:bg-amber-50 hover:text-amber-700"
                  }`}
                >
                  <span className="truncate font-medium">{l.nativeName}</span>
                  {l.code === lang && <Check className="h-3.5 w-3.5 shrink-0" />}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
